import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { bookingService } from '@/services/bookingService';
import { AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';

interface BookingStatusDialogProps {
  bookingId: string | null;
  currentStatus: string;
  isOpen: boolean;
  onClose: () => void;
  onStatusChanged: () => void;
}

export function BookingStatusDialog({ 
  bookingId, 
  currentStatus, 
  isOpen, 
  onClose, 
  onStatusChanged 
}: BookingStatusDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [cancelReason, setCancelReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const statusOptions = [
    { 
      value: 'pending', 
      label: t('pending') || 'قيد الانتظار',
      icon: Clock,
      color: 'text-yellow-600'
    },
    { 
      value: 'confirmed', 
      label: t('confirmed') || 'مؤكد',
      icon: CheckCircle,
      color: 'text-blue-600'
    },
    { 
      value: 'completed', 
      label: t('completed') || 'مكتمل',
      icon: CheckCircle,
      color: 'text-green-600'
    },
    { 
      value: 'cancelled', 
      label: t('cancelled') || 'ملغي',
      icon: XCircle,
      color: 'text-red-600'
    },
  ];

  const cancelReasons = [
    { value: 'customer_request', label: t('customerRequest') || 'طلب العميل' },
    { value: 'property_unavailable', label: t('propertyUnavailable') || 'العقار غير متاح' },
    { value: 'emergency', label: t('emergency') || 'حالة طوارئ' },
    { value: 'payment_failed', label: t('paymentFailed') || 'فشل الدفع' },
    { value: 'maintenance', label: t('maintenance') || 'صيانة' },
    { value: 'other', label: t('other') || 'أخرى' },
  ];

  const handleStatusChange = async () => {
    if (!bookingId || !selectedStatus) return;

    try {
      setIsLoading(true);

      if (selectedStatus === 'cancelled') {
        // Use cancel API for cancellation
        const finalReason = cancelReason === 'other' ? customReason.trim() : cancelReason;
        
        if (!finalReason) {
          toast({
            title: t('error') || 'خطأ',
            description: t('cancelReasonRequired') || 'سبب الإلغاء مطلوب',
            variant: 'destructive',
          });
          return;
        }

        await bookingService.cancelBookingAdmin(bookingId, finalReason);
        
        toast({
          title: t('success') || 'نجح',
          description: t('bookingCancelledSuccessfully') || 'تم إلغاء الحجز بنجاح',
        });
      } else {
        // Use status change API for other status changes
        await bookingService.changeBookingStatus(bookingId, selectedStatus);
        
        toast({
          title: t('success') || 'نجح',
          description: t('bookingStatusUpdated') || 'تم تحديث حالة الحجز بنجاح',
        });
      }

      onStatusChanged();
      onClose();
      setSelectedStatus('');
      setCancelReason('');
      setCustomReason('');
    } catch (error: any) {
      console.error('Error changing booking status:', error);
      toast({
        title: t('error') || 'خطأ',
        description: error.message || t('failedToUpdateBookingStatus') || 'فشل في تحديث حالة الحجز',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatus('');
    setCancelReason('');
    setCustomReason('');
    onClose();
  };

  const getStatusIcon = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    if (!option) return null;
    
    const Icon = option.icon;
    return <Icon className={`h-4 w-4 ${option.color}`} />;
  };

  const getStatusLabel = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option?.label || status;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            {t('changeBookingStatus') || 'تغيير حالة الحجز'}
          </DialogTitle>
          <DialogDescription>
            {t('changeBookingStatusDescription') || 'اختر الحالة الجديدة للحجز. لا يمكن التراجع عن هذا الإجراء.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Status */}
          <div className="p-3 bg-muted rounded-lg">
            <div className="text-sm font-medium text-muted-foreground mb-1">
              {t('currentStatus') || 'الحالة الحالية'}:
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(currentStatus)}
              <span className="font-medium">{getStatusLabel(currentStatus)}</span>
            </div>
          </div>

          {/* New Status Selection */}
          <div className="space-y-2">
            <Label htmlFor="status">{t('newStatus') || 'الحالة الجديدة'} *</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t('selectNewStatus') || 'اختر الحالة الجديدة'} />
              </SelectTrigger>
              <SelectContent>
                {statusOptions
                  .filter(option => option.value !== currentStatus)
                  .map((option) => {
                    const Icon = option.icon;
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${option.color}`} />
                          <span>{option.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>

          {/* Cancellation Reason (only show if cancelling) */}
          {selectedStatus === 'cancelled' && (
            <div className="space-y-2">
              <Label htmlFor="reason">{t('cancellationReason') || 'سبب الإلغاء'} *</Label>
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger>
                  <SelectValue placeholder={t('selectReason') || 'اختر السبب'} />
                </SelectTrigger>
                <SelectContent>
                  {cancelReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {cancelReason === 'other' && (
                <div className="mt-2">
                  <Label htmlFor="custom-reason">{t('customReason') || 'سبب مخصص'}</Label>
                  <Textarea
                    id="custom-reason"
                    placeholder={t('enterCustomReason') || 'أدخل السبب المخصص'}
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('cancel') || 'إلغاء'}
          </Button>
          <Button 
            onClick={handleStatusChange} 
            disabled={!selectedStatus || isLoading || (selectedStatus === 'cancelled' && (!cancelReason || (cancelReason === 'other' && !customReason.trim())))}
            className={selectedStatus === 'cancelled' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('updating') || 'جاري التحديث...'}
              </div>
            ) : (
              selectedStatus === 'cancelled' 
                ? (t('cancelBooking') || 'إلغاء الحجز')
                : (t('updateStatus') || 'تحديث الحالة')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
